import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { relocationsByCityQuery } from "@/app/Dashboard/GraphQL/relocationsByCityQuery";
import { useEffect, useId, useMemo, useState } from "react";
import L, { FitBoundsOptions } from "leaflet";
import { Marker, Polyline, ZoomControl, useMap } from "react-leaflet";
import { Map as LeafletMap } from "@/components/ui/map";
import { useLocale, useTranslations } from "use-intl";
import { BasemapLayer } from "@/components/map/BasemapLayer";

const markerIconCache = new Map<string, L.DivIcon>();

function getRelocationMarkerIcon(
  count: number,
  type: "departure" | "destination",
) {
  const key = `${type}-${count}`;
  if (!markerIconCache.has(key)) {
    const isDeparture = type === "departure";
    const size = 32;
    const bgColor = isDeparture ? "#f59e0b" : "#3b82f6";
    const textColor = "#ffffff";

    const html = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: ${bgColor};
        border: 3px solid #ffffff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        font-size: 0.75rem;
        font-weight: 700;
        color: ${textColor};
      ">
        ${count}
      </div>
    `;

    markerIconCache.set(
      key,
      L.divIcon({
        className: "relocation-map-marker-icon",
        html,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      }),
    );
  }
  return markerIconCache.get(key)!;
}

function generateArcLine(
  start: { lat: number; lng: number },
  end: { lat: number; lng: number },
) {
  const steps = 50;
  const curveFactor = 0.2;
  const points: [number, number][] = [];

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const lng = start.lng + t * (end.lng - start.lng);
    const lat = start.lat + t * (end.lat - start.lat);
    const curveOffset =
      Math.sin(Math.PI * t) * curveFactor * ((end.lng - start.lng) * 0.15);

    points.push([lat + curveOffset, lng]);
  }

  return points;
}

function getCityPosition(city?: { lat: number | null; lng: number | null }) {
  if (!city) {
    return null;
  }

  const { lat, lng } = city;
  if (typeof lat !== "number" || typeof lng !== "number") {
    return null;
  }

  return { lat, lng };
}

const FALLBACK_CENTER: L.LatLngExpression = [0, 0];

type Relocation = {
  city: { id: string; name: string; lat: number | null; lng: number | null };
  count: number;
  deliveryCities: Array<{
    city: { id: string; name: string; lat: number | null; lng: number | null };
    count: number;
  }>;
};

function MapContent({
  relocations,
  hoveredDeparture,
  setHoveredDeparture,
}: {
  relocations: Relocation[];
  hoveredDeparture: string | null;
  setHoveredDeparture: (id: string | null) => void;
}) {
  // useMap ensures we only render when map is ready
  useMap();

  const hoveredRelocation = useMemo(
    () => relocations.find((r) => r.city.id === hoveredDeparture),
    [hoveredDeparture, relocations],
  );
  const deliveryCities = hoveredRelocation?.deliveryCities ?? [];

  return (
    <>
      <BasemapLayer />
      <ZoomControl position="topright" />

      {relocations.map((relocation) => {
        if (hoveredDeparture && relocation.city.id !== hoveredDeparture) {
          return null;
        }

        const position = getCityPosition(relocation.city);
        if (!position) {
          return null;
        }

        return (
          <Marker
            key={`depart-${relocation.city.id}`}
            position={position}
            icon={getRelocationMarkerIcon(relocation.count, "departure")}
            eventHandlers={{
              mouseover: () => setHoveredDeparture(relocation.city.id),
              mouseout: () => setHoveredDeparture(null),
            }}
          />
        );
      })}

      {hoveredRelocation
        ? deliveryCities.map((deliveryCity) => {
            const position = getCityPosition(deliveryCity.city);
            if (!position) {
              return null;
            }

            return (
              <Marker
                key={`deliver-${deliveryCity.city.id}`}
                position={position}
                icon={getRelocationMarkerIcon(
                  deliveryCity.count,
                  "destination",
                )}
              />
            );
          })
        : null}

      {hoveredRelocation
        ? deliveryCities.map((deliveryCity) => {
            const start = getCityPosition(hoveredRelocation.city);
            const end = getCityPosition(deliveryCity.city);

            if (!start || !end) {
              return null;
            }

            return (
              <Polyline
                key={`${hoveredRelocation.city.id}-${deliveryCity.city.id}`}
                positions={generateArcLine(start, end)}
                pathOptions={{
                  color: "#3b82f6",
                  weight: 3,
                  dashArray: "8 12",
                  opacity: 0.9,
                }}
              />
            );
          })
        : null}
    </>
  );
}

export function RelocationMapBlock() {
  const t = useTranslations();
  const locale = useLocale();
  const mapId = useId();
  const { supplierId, supplierList } = useGlobalSupplier();
  const { data } = useSuspenseGqlQuery(relocationsByCityQuery, {
    supplierIds: supplierId ? [supplierId] : supplierList.map((s) => s.id),
  });

  const [hoveredDeparture, setHoveredDeparture] = useState<string | null>(null);

  const relocations = data.relocationsByCity as Relocation[];

  const bounds = useMemo(() => {
    const points = relocations
      .map((relocation) => getCityPosition(relocation.city))
      .filter(
        (
          position,
        ): position is {
          lat: number;
          lng: number;
        } => position !== null,
      )
      .map(({ lat, lng }) => [lat, lng] as [number, number]);

    return points.length ? L.latLngBounds(points) : null;
  }, [relocations]);

  useEffect(() => {
    setHoveredDeparture(null);
  }, [supplierId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("dashboard.blocks.relocationMap.title")}</CardTitle>
      </CardHeader>
      <CardContent className="px-2 pb-2">
        <div
          className="overflow-hidden rounded-md"
          onMouseLeave={() => {
            setHoveredDeparture(null);
          }}
        >
          <LeafletMap
            key={`${mapId}-${locale}-${supplierId ?? "all"}`}
            height="400px"
            {...(bounds
              ? {
                  bounds,
                  boundsOptions: {
                    padding: [50, 50] as [number, number],
                    maxZoom: 4,
                  } satisfies FitBoundsOptions,
                }
              : {
                  center: FALLBACK_CENTER,
                  zoom: 1,
                })}
            scrollWheelZoom={false}
          >
            <MapContent
              relocations={relocations}
              hoveredDeparture={hoveredDeparture}
              setHoveredDeparture={setHoveredDeparture}
            />
          </LeafletMap>
        </div>
      </CardContent>
    </Card>
  );
}
